import { Controller } from "@hotwired/stimulus"
import { csrfToken } from "../utils/csrf"

// The Persona character card's media strip: an Images/Videos segmented toggle
// over two pre-rendered panels, plus a full-screen viewer for a tapped tile.
//
// Switching segments is a local hidden-attribute flip — both sets are already
// on the page, so there is no request, no spinner and no JS-off cliff beyond
// "the first panel stays visible".
//
// The viewer is built in <body> rather than inline: it must cover the viewport
// from inside a scrolling sidebar (an ancestor with overflow/transform would
// otherwise clip a position:fixed child), and only one can be open at a time
// even though this controller mounts twice per page (sidebar + main column).
//
// THE OVERFLOW MENU IS BESPOKE, NOT `split-menu`
// ----------------------------------------------
// The app's dropdown idiom is the split-menu controller + .assistant-header-menu-panel
// (see app/views/apps/_settings_menu). It is the wrong tool inside this overlay,
// for reasons that are about behaviour, not taste:
//
//  1. Escape precedence. split-menu registers its own document-level keydown
//     when the menu opens. The viewer's keydown was registered earlier on the
//     same target, and document listeners fire in registration order — so one
//     Escape would close the VIEWER first (or both at once), which is exactly
//     the precedence the product asks us not to have. One handler that checks
//     `_menuOpen` before anything else is the only way to guarantee the order.
//  2. Click-outside collides. split-menu's outside-click closes only itself,
//     but the overlay's own click handler ("anything that isn't the media or
//     caption closes the viewer") would fire on the same click and take the
//     viewer down with it.
//  3. The panel is a light surface bound to the page theme; the overlay is a
//     dark scrim in BOTH themes, so it would have needed its own skin anyway.
//  4. The overlay is built imperatively and re-rendered per item (the private
//     label flips), so a Stimulus sub-controller would add a connect/disconnect
//     lifecycle to keep in sync with _render() for no gain.
//
// So: bespoke behaviour, but the same semantics and shape the rest of the app
// uses — a ⋯ toggle with aria-haspopup/aria-expanded over a role="menu" panel of
// role="menuitem" buttons, arrow-key navigable, Escape-dismissable.
export default class extends Controller {
    static targets = ["panel"]

    disconnect() {
        this.close()
    }

    // --- Segments ------------------------------------------------------------

    select(event) {
        let wanted = event.params.panel
        this.panelTargets.forEach((panel) => {
            panel.hidden = panel.dataset.personaMediaPanel !== wanted
        })
    }

    // --- Full-screen viewer --------------------------------------------------

    open(event) {
        let tile = event.currentTarget
        if (!tile?.dataset.viewerSrc) return
        event.preventDefault()

        this._items = this._tilesFor(tile)
        this._index = Math.max(0, this._items.indexOf(tile))
        this._invoker = tile

        this._overlay = this._buildOverlay()
        document.body.appendChild(this._overlay)
        // Scroll lock: the page behind a full-screen viewer must not move.
        document.body.classList.add("persona-media-viewer-open")
        this._onKeydown = (e) => this._keydown(e)
        document.addEventListener("keydown", this._onKeydown)
        this._render()
        this._overlay.querySelector(".persona-media-viewer-close")?.focus()
    }

    close() {
        if (!this._overlay) return
        this._menuOpen = false
        this._overlay.remove()
        this._overlay = null
        document.body.classList.remove("persona-media-viewer-open")
        document.removeEventListener("keydown", this._onKeydown)
        this._onKeydown = null
        // Focus goes back to the tile that opened the viewer, not to the top of
        // the document — otherwise keyboard users lose their place in the grid.
        if (this._invoker?.isConnected) this._invoker.focus()
        this._invoker = null
        this._items = []
    }

    // Tiles of the panel the clicked tile belongs to: arrow keys move within
    // the set the user is actually looking at, never across segments.
    _tilesFor(tile) {
        let panel = tile.closest("[data-persona-media-panel]") || this.element
        return Array.from(panel.querySelectorAll(".persona-media-tile"))
    }

    _buildOverlay() {
        let overlay = document.createElement("div")
        overlay.className = "persona-media-viewer"
        overlay.setAttribute("role", "dialog")
        overlay.setAttribute("aria-modal", "true")
        overlay.setAttribute("aria-label", "Media viewer")
        // ⋯ before ✕ in DOM order, so it sits to its left visually AND comes
        // first in the tab order — the destructive-ish menu is never something
        // you land on by tabbing past Close.
        overlay.innerHTML = `
            <div class="persona-media-viewer-actions">
                <button type="button" class="persona-media-viewer-btn persona-media-viewer-menu-toggle"
                        aria-label="More actions" aria-haspopup="true" aria-expanded="false">
                    <i class="bi bi-three-dots" aria-hidden="true"></i>
                </button>
                <button type="button" class="persona-media-viewer-btn persona-media-viewer-close" aria-label="Close">
                    <i class="bi bi-x-lg" aria-hidden="true"></i>
                </button>
                <div class="persona-media-viewer-menu" role="menu" aria-label="Media actions" hidden></div>
            </div>
            <figure class="persona-media-viewer-figure">
                <div class="persona-media-viewer-stage"></div>
                <figcaption class="persona-media-viewer-caption">
                    <span class="persona-media-viewer-prompt"></span>
                    <span class="persona-media-viewer-date"></span>
                </figcaption>
            </figure>`
        overlay.addEventListener("click", (e) => this._click(e))
        return overlay
    }

    // One click handler for the whole overlay. Order matters: menu concerns are
    // resolved before the "click outside closes the viewer" rule ever runs, so a
    // click aimed at the menu (or at dismissing it) can never close the viewer.
    _click(event) {
        if (event.target.closest(".persona-media-viewer-menu-toggle")) {
            event.preventDefault()
            return this._toggleMenu()
        }

        let action = event.target.closest("[data-viewer-action]")
        if (action && this._overlay.contains(action)) {
            event.preventDefault()
            return this._menuAction(action.dataset.viewerAction)
        }

        if (this._menuOpen) {
            // Click-outside dismisses the MENU only. Swallowing the event here is
            // the point: without the early return the same click would fall
            // through to the scrim rule below and close the viewer too.
            if (!event.target.closest(".persona-media-viewer-menu")) this._closeMenu()
            return
        }

        if (event.target.closest(".persona-media-viewer-close")) return this.close()
        // Click-outside: anything that isn't the media itself or its caption
        // (video controls live inside the <video>, so they're safe).
        if (!event.target.closest(".persona-media-viewer-stage, .persona-media-viewer-caption")) this.close()
    }

    // Paint the current item into the open overlay.
    _render() {
        let tile = this._items[this._index]
        if (!tile || !this._overlay) return

        let { viewerType, viewerSrc, viewerCaption, viewerDate, viewerPoster } = tile.dataset
        let stage = this._overlay.querySelector(".persona-media-viewer-stage")
        let node
        if (viewerType === "video") {
            node = document.createElement("video")
            node.controls = true
            node.preload = "metadata"
            node.playsInline = true
            // Falls back to the browser's own first-frame paint when this row
            // has no extracted poster (an older video, or a failed extraction) -
            // unreliable on iOS WebKit, but no worse than before this existed.
            if (viewerPoster) node.poster = viewerPoster
            node.src = viewerSrc
        } else {
            node = document.createElement("img")
            node.src = viewerSrc
            node.alt = viewerCaption || ""
        }
        node.className = "persona-media-viewer-media"
        stage.replaceChildren(node)

        this._overlay.querySelector(".persona-media-viewer-prompt").textContent = viewerCaption || ""
        this._overlay.querySelector(".persona-media-viewer-date").textContent = viewerDate || ""
        // Stepping to another item must not leave the previous item's menu open
        // over it — the actions would apply to something else than what's shown.
        this._closeMenu({ focusToggle: false })
    }

    _step(delta) {
        if (this._items.length < 2) return
        this._index = (this._index + delta + this._items.length) % this._items.length
        this._render()
    }

    // --- Overflow menu -------------------------------------------------------

    _menuEl() {
        return this._overlay?.querySelector(".persona-media-viewer-menu")
    }

    _toggleMenu() {
        this._menuOpen ? this._closeMenu() : this._openMenu()
    }

    _openMenu() {
        let menu = this._menuEl()
        if (!menu) return
        this._menuMode = "menu"
        this._menuError = null
        this._renderMenu()
        menu.hidden = false
        this._menuOpen = true
        this._overlay.querySelector(".persona-media-viewer-menu-toggle")?.setAttribute("aria-expanded", "true")
        this._menuItems()[0]?.focus()
    }

    _closeMenu({ focusToggle = true } = {}) {
        let menu = this._menuEl()
        if (!menu) return
        let wasOpen = this._menuOpen
        menu.hidden = true
        menu.replaceChildren()
        this._menuOpen = false
        this._menuMode = "menu"
        let toggle = this._overlay?.querySelector(".persona-media-viewer-menu-toggle")
        toggle?.setAttribute("aria-expanded", "false")
        if (wasOpen && focusToggle) toggle?.focus()
    }

    _menuItems() {
        return Array.from(this._menuEl()?.querySelectorAll("button:not([disabled])") || [])
    }

    // Two modes in one panel: the action list, and an inline delete confirmation.
    // Inline rather than window.confirm because the viewer is a body-level
    // overlay with its own focus trap — a native dialog would blow through it —
    // and because "are you sure" belongs in the same surface as the question.
    _renderMenu() {
        let menu = this._menuEl()
        let tile = this._items[this._index]
        if (!menu || !tile) return

        let isPrivate = tile.dataset.viewerPrivate === "true"
        let isVideo = tile.dataset.viewerType === "video"
        let noun = isVideo ? "video" : "image"
        let error = this._menuError
            ? `<p class="persona-media-viewer-menu-error" role="alert">${this._escape(this._menuError)}</p>`
            : ""

        if (this._menuMode === "confirm") {
            menu.innerHTML = `
                ${error}
                <p class="persona-media-viewer-menu-note">Delete this ${noun}? This can't be undone.</p>
                <button type="button" role="menuitem" class="persona-media-viewer-menu-item" data-viewer-action="cancel">
                    <i class="bi bi-arrow-left" aria-hidden="true"></i><span>Keep it</span>
                </button>
                <button type="button" role="menuitem" class="persona-media-viewer-menu-item persona-media-viewer-menu-item--danger" data-viewer-action="delete">
                    <i class="bi bi-trash3" aria-hidden="true"></i><span>Yes, delete</span>
                </button>`
            return
        }

        menu.innerHTML = `
            ${error}
            <button type="button" role="menuitem" class="persona-media-viewer-menu-item" data-viewer-action="privacy">
                <i class="bi ${isPrivate ? "bi-eye" : "bi-eye-slash"}" aria-hidden="true"></i>
                <span>${isPrivate ? "Make visible again" : "Mark as private"}</span>
            </button>
            <button type="button" role="menuitem" class="persona-media-viewer-menu-item persona-media-viewer-menu-item--danger" data-viewer-action="confirm-delete">
                <i class="bi bi-trash3" aria-hidden="true"></i><span>Delete</span>
            </button>`
    }

    _menuAction(name) {
        if (name === "cancel") {
            this._menuMode = "menu"
            this._menuError = null
            this._renderMenu()
            return this._menuItems()[0]?.focus()
        }
        if (name === "confirm-delete") {
            this._menuMode = "confirm"
            this._menuError = null
            this._renderMenu()
            // Focus "Keep it", not "Yes, delete": a stray Enter must not destroy.
            return this._menuItems()[0]?.focus()
        }
        if (name === "delete") return this._performDelete()
        if (name === "privacy") return this._performPrivacy()
    }

    async _performDelete() {
        let tile = this._items[this._index]
        if (!tile) return
        let id = tile.dataset.viewerId

        this._busy("Deleting…")
        try {
            await this._request(tile.dataset.viewerDeleteUrl, "DELETE")
        } catch (error) {
            return this._menuFailed(error)
        }
        // The row is gone: drop the tile everywhere it appears (the strip is
        // rendered twice — sidebar + main column) and shut the viewer.
        this._dismissItem(id)
    }

    async _performPrivacy() {
        let tile = this._items[this._index]
        if (!tile) return
        let wanted = tile.dataset.viewerPrivate !== "true"

        this._busy(wanted ? "Hiding…" : "Restoring…")
        let data
        try {
            data = await this._request(tile.dataset.viewerPrivacyUrl, "PATCH", { private: wanted })
        } catch (error) {
            return this._menuFailed(error)
        }

        let nowPrivate = data.private === true
        if (nowPrivate) {
            // A private row is not shown in this strip, so leaving it on screen
            // would contradict what we just told the user. Same DOM surgery as a
            // delete — the row itself is untouched and still in the Gallery.
            this._dismissItem(tile.dataset.viewerId)
            return
        }

        // Unhiding from here can only happen if a private tile ever renders in
        // the strip; keep the menu honest either way.
        this._syncTileState(tile.dataset.viewerId, "false")
        this._menuMode = "menu"
        this._menuError = null
        this._renderMenu()
        this._menuItems()[0]?.focus()
    }

    _busy(label) {
        let menu = this._menuEl()
        if (menu) menu.innerHTML = `<p class="persona-media-viewer-menu-note" role="status">${this._escape(label)}</p>`
    }

    _menuFailed(error) {
        this._menuError = error?.message || "Something went wrong."
        this._menuMode = "menu"
        this._renderMenu()
        this._menuItems()[0]?.focus()
    }

    // Close the viewer, then take the tile out of every copy of the strip and
    // repair what's left (counts, empty states, a strip with nothing in it).
    _dismissItem(id) {
        this._invoker = null // it's about to be removed; don't focus a corpse
        this._closeMenu({ focusToggle: false })
        this.close()

        let sections = new Set()
        document.querySelectorAll(`.persona-media-tile[data-viewer-id="${CSS.escape(id)}"]`).forEach((tile) => {
            let section = tile.closest(".persona-media")
            if (section) sections.add(section)
            tile.remove()
        })
        sections.forEach((section) => this._syncSection(section))
    }

    _syncTileState(id, isPrivate) {
        document.querySelectorAll(`.persona-media-tile[data-viewer-id="${CSS.escape(id)}"]`).forEach((tile) => {
            tile.dataset.viewerPrivate = isPrivate
        })
    }

    // Bring one rendered strip back in line with the tiles it still has: badge
    // counts, per-panel empty copy, and the section itself when nothing is left
    // (the server renders no strip at all in that case — match it rather than
    // leaving an empty switcher behind).
    _syncSection(section) {
        let remaining = 0
        section.querySelectorAll("[data-persona-media-panel]").forEach((panel) => {
            let key = panel.dataset.personaMediaPanel
            let grid = panel.querySelector(".persona-media-grid")
            let count = grid ? grid.querySelectorAll(".persona-media-tile").length : 0
            remaining += count

            let badge = section.querySelector(`.tj-segment input[value="${key}"]`)?.closest(".tj-segment")?.querySelector(".persona-media-count")
            if (badge) count > 0 ? (badge.textContent = count) : badge.remove()

            if (grid && count === 0) {
                grid.remove()
                let empty = document.createElement("p")
                empty.className = "persona-media-empty"
                empty.textContent = key === "videos"
                    ? "No videos yet — any video Jane makes for you will show up here."
                    : "No images yet — portraits and chat scenes land here."
                panel.appendChild(empty)
            }
        })
        if (remaining === 0) section.remove()
    }

    async _request(url, method, body) {
        let response = await fetch(url, {
            method: method,
            credentials: "same-origin",
            headers: { Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
            body: body === undefined ? undefined : JSON.stringify(body)
        })
        let data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(data.error || "Something went wrong. Try again from the Gallery.")
        return data
    }

    _escape(text) {
        let node = document.createElement("span")
        node.textContent = text
        return node.innerHTML
    }

    // --- Keyboard ------------------------------------------------------------

    _keydown(event) {
        if (!this._overlay) return

        // The menu owns the keyboard while it is open. This branch is the whole
        // Escape-precedence guarantee: Escape closes the MENU and returns, so the
        // viewer's own Escape never runs on the same press. Arrow keys are
        // swallowed too — stepping to the next image under an open menu would
        // leave the menu pointing at an item the user can no longer see.
        if (this._menuOpen) {
            if (event.key === "Escape") {
                event.preventDefault()
                event.stopPropagation()
                this._closeMenu()
            } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault()
                this._stepMenu(event.key === "ArrowDown" ? 1 : -1)
            } else if (event.key === "Tab") {
                this._trapFocus(event)
            }
            return
        }

        if (event.key === "Escape") {
            event.preventDefault()
            this.close()
        } else if (event.key === "ArrowRight") {
            event.preventDefault()
            this._step(1)
        } else if (event.key === "ArrowLeft") {
            event.preventDefault()
            this._step(-1)
        } else if (event.key === "Tab") {
            this._trapFocus(event)
        }
    }

    _stepMenu(delta) {
        let items = this._menuItems()
        if (!items.length) return
        let at = items.indexOf(document.activeElement)
        items[(at + delta + items.length) % items.length].focus()
    }

    // Keep Tab inside the dialog: the page behind it is inert while open. The
    // menu's buttons join this list for free once the panel is shown — they are
    // <button>s inside the overlay, and a hidden panel has no offsetParent, so a
    // closed menu contributes nothing.
    _trapFocus(event) {
        let focusable = Array.from(
            this._overlay.querySelectorAll('button, [href], video[controls], [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.offsetParent !== null || el === document.activeElement)
        if (!focusable.length) return

        let first = focusable[0]
        let last = focusable[focusable.length - 1]
        let active = document.activeElement
        if (!this._overlay.contains(active)) {
            event.preventDefault()
            first.focus()
        } else if (event.shiftKey && active === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && active === last) {
            event.preventDefault()
            first.focus()
        }
    }
}
